import type {
  ImportPreview,
  ImportResult,
  ManualClient,
  Merchant,
  AgreementImportPreview,
  AgreementImportResult,
  AgreementCommitCreatedItem,
  AddAgreementPayload,
  AgreementPreviewRow,
  AgreementRowInput,
  AgreementMatch,
  ImportedAgreementDetail,
  ProductOption,
  CatalogueBrand,
  CatalogueModel,
  CatalogueBrandCreateResult,
  CatalogueModelCreateResult,
  ColorOption,
} from "./types";
import type { Paginated } from "@/features/customers/types";
import { projectSchedule } from "./lib/projectSchedule";

// In-memory mock for the import feature. Mirrors the exact backend contract so
// flipping USE_MOCK to false in api.ts is the only change needed to go live.

export const mockMerchants: Merchant[] = [
  { id: 1, first_name: "Alisher", last_name: "Nazarov", phone_number: "+998901234567", client_count: 142 },
  { id: 2, first_name: "Barno", last_name: "Toshmatova", phone_number: "+998909876543", client_count: 87 },
  { id: 3, first_name: "Jasur", last_name: "Ergashev", phone_number: "+998935551234", client_count: 310 },
  { id: 4, first_name: "Dilrabo", last_name: "Yusupova", phone_number: "+998971112233", client_count: 54 },
];

export function mockGetMerchants(search?: string): Paginated<Merchant> {
  const q = (search ?? "").trim().toLowerCase();
  const results = q
    ? mockMerchants.filter(
        (m) =>
          `${m.first_name} ${m.last_name}`.toLowerCase().includes(q) ||
          m.phone_number.includes(q),
      )
    : [...mockMerchants];
  return { count: results.length, next: null, previous: null, results };
}

// Simulates a mixed preview: some ready, one exists (duplicate phone), one error.
export function mockValidate(): ImportPreview {
  return {
    ready_count: 3,
    exists_count: 1,
    error_count: 1,
    total: 5,
    rows: [
      {
        row: 1,
        first_name: "Sardor",
        last_name: "Xolmatov",
        phone_number: "+998901111111",
        pin: "31234567890123",
        birth_date: "1990-05-15",
        passport_number: "AA1234567",
        address: "Toshkent, Yunusobod",
        status: "ready",
      },
      {
        row: 2,
        first_name: "Malika",
        last_name: "Rashidova",
        phone_number: "+998902222222",
        pin: "31234567890124",
        birth_date: "1993-11-20",
        passport_number: "AB2345678",
        address: "Toshkent, Chilonzor",
        status: "ready",
      },
      {
        row: 3,
        first_name: "Otabek",
        last_name: "Karimov",
        phone_number: "+998903333333",
        pin: "31234567890125",
        birth_date: "1985-03-08",
        passport_number: "AC3456789",
        address: "Samarqand, Registon",
        status: "ready",
      },
      {
        row: 4,
        first_name: "Nigora",
        last_name: "Alieva",
        phone_number: "+998904444444",
        pin: "31234567890126",
        birth_date: "1997-07-22",
        passport_number: "AD4567890",
        address: "Buxoro, Markaziy",
        status: "exists",
        matched_field: "phone_number",
        existing: {
          id: 10001,
          first_name: "Nigora",
          last_name: "Alieva",
          phone_number: "+998904444444",
        },
      },
      {
        row: 5,
        first_name: "Bekzod",
        last_name: "",
        phone_number: "invalid",
        pin: "",
        birth_date: "",
        passport_number: "",
        address: "",
        status: "error",
        message: "phone_number: Yaroqsiz telefon raqami",
      },
    ],
  };
}

export function mockCommit(): ImportResult {
  return {
    success: true,
    imported_count: 3,
    skipped_exists: 1,
    error_count: 1,
    total: 5,
  };
}

// ── Agreement import mocks ────────────────────────────────────────────────────

// Backend `_editable_fields` parity (support_agreements.py:_EDITABLE_*): product display key first,
// then the detail keys. MOCK_DETAIL_KEYS are the subset that count toward missing_detail_fields (the
// product name is always populated, so it is never "missing").
const MOCK_EDITABLE_FIELDS: Record<string, string[]> = {
  car: ["model", "number", "vin", "engine_number", "color", "production_date", "mileage"],
  technology: ["technology_model", "serial_number", "comment"],
};
const MOCK_DETAIL_KEYS: Record<string, string[]> = {
  car: ["number", "vin", "engine_number", "color", "production_date", "mileage"],
  technology: ["serial_number", "comment"],
};

function mockEditableFields(type: string): string[] {
  return MOCK_EDITABLE_FIELDS[type] ?? [];
}

/** Mirrors the backend rows-path: seed the built defaults (color 'Oq'; TECH comment from note), then
 * whitelist-merge the operator's per-row additional_data overrides, then recompute which detail keys
 * are still empty. A filled detail key drops out of missing_detail_fields (0083/0085 parity). */
function mockMergeDetail(
  r: AgreementRowInput,
): { additional_data: Record<string, string>; missing_detail_fields: string[] } {
  const detailKeys = MOCK_DETAIL_KEYS[r.type] ?? [];
  const seeded: Record<string, string> = {};
  if (detailKeys.includes("color")) seeded.color = "Oq";
  if (r.type === "technology" && r.note) seeded.comment = r.note;
  const allowed = new Set(mockEditableFields(r.type));
  const merged: Record<string, string> = { ...seeded };
  for (const [key, value] of Object.entries(r.additional_data ?? {})) {
    if (allowed.has(key) && value != null && value !== "") merged[key] = value;
  }
  return { additional_data: merged, missing_detail_fields: detailKeys.filter((k) => !merged[k]) };
}

/** Simulates a mixed agreement preview: 3 ready (CAR + TECHNOLOGY + BUILDING), 1 exists, 1 error.
 * Catalogue-less types (building/loan/service/other) have NO match object — backend only attaches
 * match for car/technology. Their additional_data = { name: "<typed name>" }, editable_fields = []. */
export function mockValidateAgreement(): AgreementImportPreview {
  const preview: AgreementImportPreview = {
    ready_count: 3,
    exists_count: 1,
    error_count: 1,
    total: 5,
    warnings: null,
    errors: null,
    rows: [
      {
        row: 1,
        identifier: "AGR-2024-001",
        type: "car",
        name: "Chevrolet Cobalt",
        cost_price: "78000000",
        selling_price: "85000000",
        down_payment: "17000000",
        payment_count: 12,
        monthly_payment: "5666667",
        paid_amount: "0",
        first_payment_date: "2024-02-01",
        payment_day: 1,
        note: "",
        client: { id: 201, label: "Sardor Xolmatov" },
        message: "",
        status: "ready",
        client_id: 201,
        // Pre-filled from the Excel file: number/vin/color read off the sheet; engine_number/production_date/mileage blank.
        additional_data: { number: "01A777AA", vin: "WVWZZZ3CZDE123456", color: "Oq" },
        editable_fields: MOCK_EDITABLE_FIELDS.car,
        missing_detail_fields: ["engine_number", "production_date", "mileage"],
        // Exact catalogue match: typed name matched "Chevrolet Cobalt" directly.
        match: {
          status: "matched",
          score: 1.0,
          canonical: "Chevrolet Cobalt",
          model_id: 1,
          brand: "Chevrolet",
          suggestion: null,
          typed: "Chevrolet Cobalt",
        },
        // Example: 3-day pre-due reminder for this row.
        notification_before: 3,
      },
      {
        row: 2,
        identifier: "AGR-2024-002",
        type: "technology",
        name: "Samsung Galaxy S24 Ultra",
        cost_price: "11000000",
        selling_price: "12500000",
        down_payment: "2500000",
        payment_count: 6,
        monthly_payment: "1666667",
        paid_amount: "2500000",
        first_payment_date: "2024-03-15",
        payment_day: 15,
        note: "Aksiya narxi",
        client: { id: 202, label: "Malika Rashidova" },
        message: "",
        status: "ready",
        client_id: 202,
        // Pre-filled from the Excel file: serial_number read off the sheet; comment seeded from note.
        additional_data: { serial_number: "SN-2024-XR99", comment: "Aksiya narxi" },
        editable_fields: MOCK_EDITABLE_FIELDS.technology,
        missing_detail_fields: [],
        // Fuzzy suggestion: typed "kobolat", backend suggests "Chevrolet Cobalt".
        match: {
          status: "suggested",
          score: 0.72,
          canonical: "Chevrolet Cobalt",
          model_id: 1,
          brand: "Chevrolet",
          suggestion: "Chevrolet Cobalt",
          typed: "kobolat",
        },
      },
      {
        // Catalogue-less type: backend sends NO match object. additional_data = { name } only.
        row: 3,
        identifier: "AGR-2024-003",
        type: "building",
        name: "3 xonali kvartira Yunusobodda",
        cost_price: "120000000",
        selling_price: "150000000",
        down_payment: "30000000",
        payment_count: 24,
        monthly_payment: "5000000",
        paid_amount: "10000000",
        first_payment_date: "2024-02-01",
        payment_day: 1,
        note: "",
        client: { id: 203, label: "Otabek Karimov" },
        message: "",
        status: "ready",
        client_id: 203,
        additional_data: { name: "3 xonali kvartira Yunusobodda" },
        editable_fields: [],
        missing_detail_fields: [],
        // No match — catalogue-less types never carry a match object from the backend.
      },
      {
        row: 4,
        identifier: "AGR-2024-004",
        type: "loan",
        name: "Naqd qarz",
        cost_price: null,
        selling_price: null,
        down_payment: "0",
        payment_count: 3,
        monthly_payment: "1000000",
        paid_amount: "1000000",
        first_payment_date: "2024-01-10",
        payment_day: 10,
        note: "",
        client: { id: 204, label: "Nodira Karimova" },
        message: "Shartnoma allaqachon mavjud",
        status: "exists",
        existing: { id: 5001, agreement_code: "AGR-2024-004" },
        // Catalogue-less type: no match object (backend only attaches match for car/technology).
      },
      {
        row: 5,
        identifier: "",
        type: "other",
        name: "",
        cost_price: null,
        selling_price: null,
        down_payment: "0",
        payment_count: 0,
        monthly_payment: null,
        paid_amount: "0",
        first_payment_date: null,
        payment_day: null,
        note: "",
        client: null,
        message: "identifier: Majburiy maydon bo'sh",
        status: "error",
      },
    ],
  };
  // Attach the projected schedule + remainder to ready rows (0087) — same as the rows-path mock.
  for (const row of preview.rows) {
    if (row.status !== "ready") continue;
    const projection = mockProjectedSchedule(row);
    if (projection) {
      row.payments = projection.payments;
      row.remainder = projection.remainder;
    }
  }
  return preview;
}

/** Simulates a successful agreement commit. */
export function mockCommitAgreement(): AgreementImportResult {
  return {
    success: true,
    imported_count: 3,
    skipped_exists: 1,
    error_count: 1,
    total: 5,
    warnings: null,
    errors: null,
    created: [
      {
        loan_id: 9001,
        agreement_code: "AGR-2024-001",
        client_label: "Sardor Xolmatov",
        type: "car",
        // engine_number/production_date/mileage were blank in the file; number/vin/color were pre-filled.
        missing_detail_fields: ["engine_number", "production_date", "mileage"],
      },
      {
        loan_id: 9002,
        agreement_code: "AGR-2024-002",
        client_label: "Malika Rashidova",
        type: "technology",
        // All detail fields pre-filled from the file.
        missing_detail_fields: [],
      },
      {
        loan_id: 9003,
        agreement_code: "AGR-2024-003",
        client_label: "Otabek Karimov",
        type: "building",
        // Catalogue-less types have no editable detail fields.
        missing_detail_fields: [],
      },
    ],
  };
}

// ── Imported agreement detail mocks ──────────────────────────────────────────

/**
 * Returns a mock detail record for a freshly-imported agreement.
 * Odd loanId → CAR (6 editable fields, 2 missing).
 * Even loanId → TECHNOLOGY (2 editable fields, 1 missing).
 * This lets the demo cover both branches without switching mocks.
 */
export function mockGetImportedAgreement(loanId: number): ImportedAgreementDetail {
  const isCar = loanId % 2 !== 0;

  if (isCar) {
    return {
      id: loanId,
      agreement_code: `AGR-2024-00${loanId}`,
      type: "car",
      status: "active",
      client: { id: 201, label: "Sardor Xolmatov", phone_number: "+998901111111" },
      merchant: { id: 1, label: "Alisher Nazarov" },
      selling_price: 85000000,
      down_payment: 17000000,
      monthly_payment: 5666667,
      payment_count: 12,
      // 3 months fully paid + 1 partial → paid = down(17M) + 3×5.67M + 3M partial.
      paid_amount: 37000000,
      payment_day: 1,
      first_payment_date: "2024-02-01",
      additional_data: {
        number: "01A777AA",
        vin: "WVWZZZ3CZDE123456",
        engine_number: "",
        color: "",
        production_date: "",
        mileage: "",
      },
      voucher_text: null,
      imported_from_file: true,
      created_time: "2024-01-15T10:30:00Z",
      // CAR editable fields per backend spec.
      editable_fields: ["number", "vin", "engine_number", "color", "production_date", "mileage"],
      // Fields still empty after import.
      missing_detail_fields: ["engine_number", "color", "production_date", "mileage"],
      // Real schedule (Feature A): 3 paid (green), 1 partial (amber), 8 unpaid (grey); last absorbs remainder.
      // Statuses mirror the real backend contract: PaymentStatus TextChoices *values* (lowercase).
      payments: [
        { number: 1, status: "done", amount: "5666667", paid: "5666667", payment_date: "2024-02-01", paid_date: "2024-02-01" },
        { number: 2, status: "done", amount: "5666667", paid: "5666667", payment_date: "2024-03-01", paid_date: "2024-03-02" },
        { number: 3, status: "done", amount: "5666667", paid: "5666667", payment_date: "2024-04-01", paid_date: "2024-04-01" },
        // Partial: 2 000 000 of 5 666 667 paid (child payment exists); demonstrates proportional amber fill.
        { number: 4, status: "delayed_with_child_payments", amount: "5666667", paid: "2000000", payment_date: "2024-05-01", paid_date: null },
        { number: 5, status: "active", amount: "5666667", paid: "0", payment_date: "2024-06-01", paid_date: null },
        { number: 6, status: "pending", amount: "5666667", paid: "0", payment_date: "2024-07-01", paid_date: null },
        { number: 7, status: "pending", amount: "5666667", paid: "0", payment_date: "2024-08-01", paid_date: null },
        { number: 8, status: "pending", amount: "5666667", paid: "0", payment_date: "2024-09-01", paid_date: null },
        { number: 9, status: "pending", amount: "5666667", paid: "0", payment_date: "2024-10-01", paid_date: null },
        { number: 10, status: "pending", amount: "5666667", paid: "0", payment_date: "2024-11-01", paid_date: null },
        { number: 11, status: "pending", amount: "5666667", paid: "0", payment_date: "2024-12-01", paid_date: null },
        { number: 12, status: "pending", amount: "5666663", paid: "0", payment_date: "2025-01-01", paid_date: null },
      ],
      remainder: "48000000",
    };
  }

  // TECHNOLOGY branch
  return {
    id: loanId,
    agreement_code: `AGR-2024-00${loanId}`,
    type: "technology",
    status: "active",
    client: { id: 202, label: "Malika Rashidova", phone_number: "+998902222222" },
    merchant: { id: 1, label: "Alisher Nazarov" },
    selling_price: 12500000,
    down_payment: 2500000,
    monthly_payment: 1666667,
    payment_count: 6,
    paid_amount: 2500000,
    payment_day: 15,
    first_payment_date: "2024-03-15",
    additional_data: {
      serial_number: "",
      comment: "Samsung Galaxy S24 Ultra",
    },
    voucher_text: null,
    imported_from_file: true,
    created_time: "2024-01-15T10:31:00Z",
    // TECHNOLOGY editable fields per backend spec.
    editable_fields: ["serial_number", "comment"],
    // serial_number is still missing.
    missing_detail_fields: ["serial_number"],
    // Only the down payment is in — all 6 installments still unpaid (grey); last absorbs remainder.
    payments: [
      { number: 1, status: "active", amount: "1666667", paid: "0", payment_date: "2024-03-15", paid_date: null },
      { number: 2, status: "pending", amount: "1666667", paid: "0", payment_date: "2024-04-15", paid_date: null },
      { number: 3, status: "pending", amount: "1666667", paid: "0", payment_date: "2024-05-15", paid_date: null },
      { number: 4, status: "pending", amount: "1666667", paid: "0", payment_date: "2024-06-15", paid_date: null },
      { number: 5, status: "pending", amount: "1666667", paid: "0", payment_date: "2024-07-15", paid_date: null },
      { number: 6, status: "pending", amount: "1666665", paid: "0", payment_date: "2024-08-15", paid_date: null },
    ],
    remainder: "10000000",
  };
}

/**
 * Simulates a successful PATCH by merging the submitted additional_data into the stored record.
 * Returns the updated detail shape so the hook can invalidate / refresh the UI.
 */
export function mockUpdateImportedAgreement(
  loanId: number,
  data: Record<string, string>,
): ImportedAgreementDetail {
  const base = mockGetImportedAgreement(loanId);
  const merged = { ...base.additional_data, ...data };
  // Recalculate missing_detail_fields: any editable field whose merged value is empty.
  const missing = base.editable_fields.filter(
    (k) => !merged[k] || String(merged[k]).trim() === "",
  );
  return {
    ...base,
    additional_data: merged,
    missing_detail_fields: missing,
  };
}

// ── Product catalogue options mock ───────────────────────────────────────────

const MOCK_CAR_OPTIONS: ProductOption[] = [
  { id: 1, name: "Chevrolet Cobalt", brand: "Chevrolet" },
  { id: 2, name: "Chevrolet Onix", brand: "Chevrolet" },
  { id: 3, name: "Toyota Corolla", brand: "Toyota" },
  { id: 4, name: "Toyota Camry", brand: "Toyota" },
];

const MOCK_TECH_OPTIONS: ProductOption[] = [
  { id: 101, name: "Samsung Galaxy S24 Ultra", brand: "Samsung" },
  { id: 102, name: "iPhone 15 Pro", brand: "Apple" },
  { id: 103, name: "Xiaomi 14", brand: "Xiaomi" },
];

/** Returns mock product catalogue options for a given loan type. */
export function mockGetProductOptions(type: "car" | "technology"): ProductOption[] {
  return type === "car" ? [...MOCK_CAR_OPTIONS] : [...MOCK_TECH_OPTIONS];
}

// ── Color catalogue mock (subtask 03 / 05) ───────────────────────────────────
// Mutable store so colour CRUD (catalogue feature, subtask 05) operates on the same list that
// the import detail drawer reads — no divergence between the two features in mock mode.

let MOCK_COLORS: ColorOption[] = [
  { id: 1, name: "Oq" },
  { id: 2, name: "Qora" },
  { id: 3, name: "Kulrang" },
  { id: 4, name: "Kumush" },
  { id: 5, name: "Qizil" },
  { id: 6, name: "Ko'k" },
  { id: 7, name: "Yashil" },
  { id: 8, name: "Sariq" },
];
let nextColorId = 100;

/** Returns all colours sorted alphabetically (mirrors the backend's default ordering). */
export function mockGetColors(): ColorOption[] {
  return [...MOCK_COLORS].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Idempotent create: returns created=false if a colour with the same name already exists
 * (case-insensitive), otherwise inserts and returns created=true.
 */
export function mockCreateColor(name: string): ColorOption & { created: boolean } {
  const clean = name.trim();
  if (!clean) throw { importCode: "generic", message: "name required" };
  const existing = MOCK_COLORS.find((c) => c.name.toLowerCase() === clean.toLowerCase());
  if (existing) return { ...existing, created: false };
  const color: ColorOption = { id: ++nextColorId, name: clean };
  MOCK_COLORS.push(color);
  return { ...color, created: true };
}

/** Update a colour's name (rejects duplicates, case-insensitive, excluding itself). */
export function mockUpdateColor(id: number, name: string): ColorOption {
  const clean = name.trim();
  if (!clean) throw { importCode: "generic", message: "name required" };
  const color = MOCK_COLORS.find((c) => c.id === id);
  if (!color) throw { importCode: "generic", message: "color not found" };
  const dup = MOCK_COLORS.find(
    (c) => c.id !== id && c.name.toLowerCase() === clean.toLowerCase(),
  );
  if (dup) throw { importCode: "generic", message: "duplicate name" };
  color.name = clean;
  return { id: color.id, name: color.name };
}

/** Remove a colour from the store (no-op guard: throws if id not found). */
export function mockDeleteColor(id: number): void {
  const idx = MOCK_COLORS.findIndex((c) => c.id === id);
  if (idx === -1) throw { importCode: "generic", message: "color not found" };
  MOCK_COLORS.splice(idx, 1);
}

// ── Inline catalogue-add mock (subtask 08) ───────────────────────────────────
// Mutable brand/model lists so a created brand/model shows up immediately on the next list fetch.
// `canonical` is kept in lockstep with MOCK_*_OPTIONS so that picking a freshly-added model and
// re-validating (mockMatchName reads those options) flips the row to "matched" offline.

interface MockBrand {
  id: number;
  name: string;
}
interface MockModel {
  id: number;
  brandId: number;
  name: string;
  canonical: string;
}

const MOCK_CAR_BRANDS: MockBrand[] = [
  { id: 1, name: "Chevrolet" },
  { id: 2, name: "Toyota" },
];
const MOCK_CAR_MODELS: MockModel[] = [
  { id: 11, brandId: 1, name: "Cobalt", canonical: "Chevrolet Cobalt" },
  { id: 12, brandId: 1, name: "Onix", canonical: "Chevrolet Onix" },
  { id: 13, brandId: 2, name: "Corolla", canonical: "Toyota Corolla" },
  { id: 14, brandId: 2, name: "Camry", canonical: "Toyota Camry" },
];
const MOCK_TECH_BRANDS: MockBrand[] = [
  { id: 101, name: "Samsung" },
  { id: 102, name: "Apple" },
  { id: 103, name: "Xiaomi" },
];
const MOCK_TECH_MODELS: MockModel[] = [
  { id: 111, brandId: 101, name: "Galaxy S24 Ultra", canonical: "Samsung Galaxy S24 Ultra" },
  { id: 112, brandId: 102, name: "iPhone 15 Pro", canonical: "iPhone 15 Pro" },
  { id: 113, brandId: 103, name: "14", canonical: "Xiaomi 14" },
];

let nextCatalogueId = 1000;

function catalogueFor(type: "car" | "technology") {
  return type === "car"
    ? { brands: MOCK_CAR_BRANDS, models: MOCK_CAR_MODELS, options: MOCK_CAR_OPTIONS }
    : { brands: MOCK_TECH_BRANDS, models: MOCK_TECH_MODELS, options: MOCK_TECH_OPTIONS };
}

// Mirror backend product_matching.canonical_name: keep the raw model name if it already starts with
// the brand, otherwise prefix with "Brand ".
function mockCanonical(brandName: string, modelName: string): string {
  const b = brandName.trim();
  const m = modelName.trim();
  return m.toLowerCase().startsWith(b.toLowerCase()) ? m : `${b} ${m}`;
}

export function mockGetBrands(type: "car" | "technology"): CatalogueBrand[] {
  return catalogueFor(type)
    .brands.map((b) => ({ id: b.id, name: b.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function mockGetBrandModels(
  type: "car" | "technology",
  brandId: number,
): CatalogueModel[] {
  return catalogueFor(type)
    .models.filter((m) => m.brandId === brandId)
    .map((m) => ({ id: m.id, name: m.name, canonical: m.canonical }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function mockCreateBrand(
  type: "car" | "technology",
  name: string,
): CatalogueBrandCreateResult {
  const clean = name.trim();
  if (!clean) throw { importCode: "generic", message: "name required" };
  const { brands } = catalogueFor(type);
  const existing = brands.find((b) => b.name.toLowerCase() === clean.toLowerCase());
  if (existing) return { id: existing.id, name: existing.name, created: false };
  const brand: MockBrand = { id: ++nextCatalogueId, name: clean };
  brands.push(brand);
  return { id: brand.id, name: brand.name, created: true };
}

export function mockCreateModel(
  type: "car" | "technology",
  brandId: number,
  name: string,
): CatalogueModelCreateResult {
  const clean = name.trim();
  if (!clean) throw { importCode: "generic", message: "name required" };
  const { brands, models, options } = catalogueFor(type);
  const brand = brands.find((b) => b.id === brandId);
  if (!brand) throw { importCode: "generic", message: "brand not found" };
  const existing = models.find(
    (m) => m.brandId === brandId && m.name.toLowerCase() === clean.toLowerCase(),
  );
  if (existing) {
    return {
      id: existing.id,
      name: existing.name,
      brand: brand.name,
      canonical: existing.canonical,
      created: false,
    };
  }
  const canonical = mockCanonical(brand.name, clean);
  const model: MockModel = { id: ++nextCatalogueId, brandId, name: clean, canonical };
  models.push(model);
  // Keep the matcher's option list in sync so a re-validate after picking flips the row to matched.
  options.push({ id: model.id, name: canonical, brand: brand.name });
  return { id: model.id, name: model.name, brand: brand.name, canonical, created: true };
}

// ── Catalogue mutators (used by catalogue feature via re-export) ─────────────

export function mockUpdateBrand(
  type: "car" | "technology",
  id: number,
  name: string,
): CatalogueBrand {
  const clean = name.trim();
  if (!clean) throw { importCode: "generic", message: "name required" };
  const { brands, models } = catalogueFor(type);
  const brand = brands.find((b) => b.id === id);
  if (!brand) throw { importCode: "generic", message: "brand not found" };
  // Check for duplicate name (excluding this brand itself).
  const dup = brands.find((b) => b.id !== id && b.name.toLowerCase() === clean.toLowerCase());
  if (dup) throw { importCode: "generic", message: "duplicate name" };
  const oldName = brand.name;
  brand.name = clean;
  // Re-derive canonical strings for all child models when the brand name changes.
  for (const m of models.filter((m) => m.brandId === id)) {
    m.canonical = mockCanonical(clean, m.name);
    // Also keep MOCK_*_OPTIONS in sync.
    const { options } = catalogueFor(type);
    const opt = options.find((o) => o.brand === oldName && o.name === `${oldName} ${m.name}` || o.name === m.canonical);
    if (opt) {
      opt.brand = clean;
      opt.name = m.canonical;
    }
  }
  return { id: brand.id, name: brand.name };
}

export function mockDeleteBrand(type: "car" | "technology", id: number): void {
  const { brands, models, options } = catalogueFor(type);
  const idx = brands.findIndex((b) => b.id === id);
  if (idx === -1) throw { importCode: "generic", message: "brand not found" };
  const childIds = new Set(models.filter((m) => m.brandId === id).map((m) => m.id));
  // Cascade: remove child models and their options.
  for (const childId of childIds) {
    const mi = models.findIndex((m) => m.id === childId);
    if (mi !== -1) models.splice(mi, 1);
    const oi = options.findIndex((o) => o.id === childId);
    if (oi !== -1) options.splice(oi, 1);
  }
  brands.splice(idx, 1);
}

export function mockUpdateModel(
  type: "car" | "technology",
  id: number,
  name: string,
): { id: number; name: string; canonical: string } {
  const clean = name.trim();
  if (!clean) throw { importCode: "generic", message: "name required" };
  const { brands, models, options } = catalogueFor(type);
  const model = models.find((m) => m.id === id);
  if (!model) throw { importCode: "generic", message: "model not found" };
  // Check for duplicate name within the same brand.
  const dup = models.find(
    (m) => m.id !== id && m.brandId === model.brandId && m.name.toLowerCase() === clean.toLowerCase(),
  );
  if (dup) throw { importCode: "generic", message: "duplicate name" };
  const brand = brands.find((b) => b.id === model.brandId);
  model.name = clean;
  model.canonical = mockCanonical(brand?.name ?? "", clean);
  // Keep options in sync.
  const opt = options.find((o) => o.id === id);
  if (opt) opt.name = model.canonical;
  return { id: model.id, name: model.name, canonical: model.canonical };
}

export function mockDeleteModel(type: "car" | "technology", id: number): void {
  const { models, options } = catalogueFor(type);
  const idx = models.findIndex((m) => m.id === id);
  if (idx === -1) throw { importCode: "generic", message: "model not found" };
  models.splice(idx, 1);
  const oi = options.findIndex((o) => o.id === id);
  if (oi !== -1) options.splice(oi, 1);
}

// ── Editable rows mock (subtask 06) ──────────────────────────────────────────
// Re-derives a preview from hand-edited rows so the editable table works in mock mode. Not a real
// validator — it only checks identifier presence and recomputes the model-match badge by name, which
// is enough to exercise the debounce / re-colour / catalogue-add (subtask 08) flows offline.

function mockMatchName(type: string, name: string): AgreementMatch | undefined {
  if (type !== "car" && type !== "technology") return undefined;
  const options = type === "car" ? MOCK_CAR_OPTIONS : MOCK_TECH_OPTIONS;
  const typed = name.trim();
  if (!typed) {
    return { status: "unmatched", score: 0, canonical: null, model_id: null, brand: null, suggestion: null, typed };
  }
  const exact = options.find((o) => o.name.toLowerCase() === typed.toLowerCase());
  if (exact) {
    return { status: "matched", score: 1, canonical: exact.name, model_id: exact.id, brand: exact.brand, suggestion: null, typed };
  }
  // Crude "shares the first word" suggestion so editing toward a catalogue name nudges to "suggested".
  const firstWord = typed.toLowerCase().split(/\s+/)[0];
  const partial = options.find((o) => firstWord.length >= 3 && o.name.toLowerCase().includes(firstWord));
  if (partial) {
    return { status: "suggested", score: 0.72, canonical: partial.name, model_id: partial.id, brand: partial.brand, suggestion: partial.name, typed };
  }
  return { status: "unmatched", score: 0, canonical: null, model_id: null, brand: null, suggestion: null, typed };
}

// ── Projected payment schedule (delegated to lib/projectSchedule, task 0121.04) ──
// projectSchedule() is the extracted pure helper; mock.ts re-exports the same logic.
// When calling from an AgreementPreviewRow (which has numeric payment_count), we convert to string.
function mockProjectedSchedule(
  row: Pick<
    AgreementPreviewRow,
    "selling_price" | "down_payment" | "monthly_payment" | "payment_count" | "paid_amount" | "first_payment_date"
  >,
): ReturnType<typeof projectSchedule> {
  return projectSchedule({
    selling_price: row.selling_price ?? "",
    down_payment: row.down_payment ?? "",
    monthly_payment: row.monthly_payment ?? "",
    payment_count: String(row.payment_count ?? ""),
    paid_amount: row.paid_amount ?? "",
    first_payment_date: row.first_payment_date ?? "",
  });
}

function mockRowFromInput(r: AgreementRowInput, index: number): AgreementPreviewRow {
  const hasIdentifier = r.identifier.trim() !== "";
  const base: AgreementPreviewRow = {
    row: index + 1,
    identifier: r.identifier,
    type: r.type,
    name: r.name,
    cost_price: r.cost_price || null,
    selling_price: r.selling_price || null,
    down_payment: r.down_payment || "0",
    payment_count: Number(r.payment_count) || 0,
    monthly_payment: r.monthly_payment || null,
    paid_amount: r.paid_amount || "0",
    first_payment_date: r.first_payment_date || null,
    payment_day: r.payment_day ? Number(r.payment_day) : null,
    note: r.note,
    client: hasIdentifier ? { id: 200 + index, label: `Mijoz ${r.identifier}` } : null,
    message: hasIdentifier ? "" : "identifier: Majburiy maydon bo'sh",
    status: hasIdentifier ? "ready" : "error",
    match: mockMatchName(r.type, r.name),
    // Round-trip: convert raw string back to number|null for the preview row shape.
    notification_before: r.notification_before !== "" ? Number(r.notification_before) : null,
  };
  if (hasIdentifier) {
    base.client_id = 200 + index;
    base.editable_fields = mockEditableFields(r.type);
    const detail = mockMergeDetail(r);
    base.additional_data = detail.additional_data;
    base.missing_detail_fields = detail.missing_detail_fields;
    // Projected schedule + remainder so the preview drawer's progress bar updates on money edits (0087).
    const projection = mockProjectedSchedule(base);
    if (projection) {
      base.payments = projection.payments;
      base.remainder = projection.remainder;
    }
  }
  return base;
}

/** Re-validates hand-edited rows (dry-run) — mirrors the JSON rows-path response shape. */
export function mockValidateAgreementRows(rows: AgreementRowInput[]): AgreementImportPreview {
  const previewRows = rows.map(mockRowFromInput);
  return {
    ready_count: previewRows.filter((r) => r.status === "ready").length,
    exists_count: previewRows.filter((r) => r.status === "exists").length,
    error_count: previewRows.filter((r) => r.status === "error").length,
    total: previewRows.length,
    warnings: null,
    errors: null,
    rows: previewRows,
  };
}

/** Commits hand-edited rows — only rows with an identifier become "created". */
export function mockCommitAgreementRows(rows: AgreementRowInput[]): AgreementImportResult {
  const ready = rows.filter((r) => r.identifier.trim() !== "");
  return {
    success: true,
    imported_count: ready.length,
    skipped_exists: 0,
    error_count: rows.length - ready.length,
    total: rows.length,
    warnings: null,
    errors: null,
    created: ready.map((r, i) => ({
      loan_id: 9100 + i,
      agreement_code: r.identifier,
      client_label: `Mijoz ${r.identifier}`,
      type: r.type,
      missing_detail_fields: mockMergeDetail(r).missing_detail_fields,
    })),
  };
}

// Simulates phone_exists error for a sentinel phone number.
const SENTINEL_PHONE = "+998900000000";

export function mockAddClient(
  _merchantId: number,
  payload: ManualClient,
): { id: number; first_name: string; last_name: string; phone_number: string } {
  if (payload.phone_number === SENTINEL_PHONE) {
    // Tag the error so api.ts boundary can normalise it into an ImportError.
    const err = new Error("phone_exists") as Error & { importCode: string };
    err.importCode = "phone_exists";
    throw err;
  }
  return {
    id: Math.floor(Math.random() * 90000) + 10000,
    first_name: payload.first_name,
    last_name: payload.last_name,
    phone_number: payload.phone_number,
  };
}

// Simulates a duplicate-agreement error for a sentinel name.
const SENTINEL_AGREEMENT_NAME = "DUPLICATE";

/**
 * Mock for POST /support/merchants/{id}/add-agreement/ (0119).
 * Returns the created loan as an AgreementCommitCreatedItem (same shape the import commit returns).
 * Error simulation: the inline-new-client sentinel phone surfaces a phone_exists tagged error; a
 * sentinel agreement name surfaces a generic tagged error — both flow through api.ts's toImportError.
 */
export function mockAddAgreement(
  _merchantId: number,
  payload: AddAgreementPayload,
): AgreementCommitCreatedItem {
  // Inline-new-client duplicate phone (mirrors mockAddClient's sentinel).
  if (payload.new_client?.phone_number === SENTINEL_PHONE) {
    const err = new Error("phone_exists") as Error & { importCode: string };
    err.importCode = "phone_exists";
    throw err;
  }
  if (payload.name.trim().toUpperCase() === SENTINEL_AGREEMENT_NAME) {
    const err = new Error("duplicate agreement") as Error & { importCode: string };
    err.importCode = "generic";
    throw err;
  }

  // Reuse the rows-path detail merge so missing_detail_fields matches the import path.
  const synthetic: AgreementRowInput = {
    identifier: payload.new_client?.phone_number ?? `client#${payload.client_id ?? 0}`,
    type: payload.type,
    name: payload.name,
    cost_price: payload.cost_price,
    selling_price: payload.selling_price,
    down_payment: payload.down_payment,
    monthly_payment: payload.monthly_payment,
    payment_count: payload.payment_count,
    first_payment_date: payload.first_payment_date,
    payment_day: payload.payment_day,
    paid_amount: payload.paid_amount,
    note: payload.note,
    additional_data: payload.additional_data,
    // Thread notification_before through so the round-trip is preserved (value unused by detail merge).
    notification_before:
      payload.notification_before != null ? String(payload.notification_before) : "",
  };
  const label = payload.new_client
    ? `${payload.new_client.first_name} ${payload.new_client.last_name}`.trim()
    : `Mijoz #${payload.client_id}`;
  return {
    loan_id: Math.floor(Math.random() * 90000) + 9200,
    agreement_code: `AGR-${Math.floor(Math.random() * 900000) + 100000}`,
    client_label: label,
    type: payload.type,
    missing_detail_fields: mockMergeDetail(synthetic).missing_detail_fields,
  };
}
